/**
 * Feito pelo agente netexperts
 * Módulo: AnalyticsObra — configuração declarativa por área
 * Data: 2026-06-14
 */
import {
    PresentationChartLineIcon,
    ArchiveBoxIcon,
    ShoppingCartIcon,
    ArrowDownCircleIcon,
    ClockIcon,
    ArrowsRightLeftIcon,
    MagnifyingGlassIcon,
    UsersIcon,
    DocumentChartBarIcon,
} from '@heroicons/react/24/outline'
import { formatCurrency, formatDate, formatNumber } from '../../../../utils/obraAnalytics'

export const TABS = [
    { id: 'executivo', label: 'Executivo', icon: PresentationChartLineIcon },
    { id: 'estoque', label: 'Estoque', icon: ArchiveBoxIcon },
    { id: 'vendas', label: 'Vendas', icon: ShoppingCartIcon },
    { id: 'recebidos', label: 'Recebidos', icon: ArrowDownCircleIcon },
    { id: 'a-receber', label: 'A Receber', icon: ClockIcon },
    { id: 'movimentacao', label: 'Movimentação', icon: ArrowsRightLeftIcon },
]

// Aba utilitária do investidor (download de relatórios liberados).
export const INVESTOR_UTILITY_TABS = [
    { id: 'relatorios-obra', label: 'Relatórios', icon: DocumentChartBarIcon },
]

// Abas exclusivas do admin — não exibidas no painel do investidor.
export const ADMIN_TABS = [
    { id: 'investidores-obra', label: 'Investidores', icon: UsersIcon },
    { id: 'relatorios-obra', label: 'Relatórios', icon: DocumentChartBarIcon },
    { id: 'busca', label: 'Execuções de busca', icon: MagnifyingGlassIcon },
]

// Banner: gradiente aplicado via `style` inline (cores hex) para NÃO depender do purge do
// Tailwind — este arquivo é .js e não é varrido pelo content glob (./src/**/*.jsx).
// `grad` = [cor inicial, cor final] (tons escuros p/ contraste com o texto branco).
export const TAB_THEME = {
    executivo: { grad: ['#1f2937', '#111827'], soft: 'bg-gray-50 border-gray-100', text: 'text-gray-700', accent: 'bg-gray-900' },
    estoque: { grad: ['#047857', '#064e3b'], soft: 'bg-emerald-50 border-emerald-100', text: 'text-emerald-700', accent: 'bg-emerald-600' },
    vendas: { grad: ['#1d4ed8', '#312e81'], soft: 'bg-blue-50 border-blue-100', text: 'text-blue-700', accent: 'bg-blue-600' },
    recebidos: { grad: ['#0e7490', '#0c4a6e'], soft: 'bg-cyan-50 border-cyan-100', text: 'text-cyan-700', accent: 'bg-cyan-600' },
    'a-receber': { grad: ['#b45309', '#9a3412'], soft: 'bg-amber-50 border-amber-100', text: 'text-amber-700', accent: 'bg-amber-600' },
    movimentacao: { grad: ['#be123c', '#831843'], soft: 'bg-rose-50 border-rose-100', text: 'text-rose-700', accent: 'bg-rose-600' },
    busca: { grad: ['#6d28d9', '#4c1d95'], soft: 'bg-violet-50 border-violet-100', text: 'text-violet-700', accent: 'bg-violet-600' },
    'investidores-obra': { grad: ['#0f766e', '#134e4a'], soft: 'bg-teal-50 border-teal-100', text: 'text-teal-700', accent: 'bg-teal-600' },
    'relatorios-obra': { grad: ['#4338ca', '#312e81'], soft: 'bg-indigo-50 border-indigo-100', text: 'text-indigo-700', accent: 'bg-indigo-600' },
}

export const TAB_DESCRIPTION = {
    executivo: 'Visão consolidada da obra — cruzamento de KPIs de todas as áreas.',
    estoque: 'Mix de unidades por status e nível de comprometimento do inventário.',
    vendas: 'Performance comercial com análise mês a mês: VGV, recebido e a receber por contrato.',
    recebidos: 'Caixa efetivamente depositado no período.',
    'a-receber': 'Carteira em aberto (período + 12 meses), atraso por tempo e previsão de vencimentos.',
    movimentacao: 'S0 + DRE = S1 (obra inteira). Resultado = saldo depois − saldo antes.',
    busca: 'Execuções agendadas de sincronização de dados desta obra com o ERP.',
    'investidores-obra': 'Investidores vinculados a esta obra e sua participação no investimento.',
    'relatorios-obra': 'Relatórios Excel gerados para esta obra — baixe os arquivos disponíveis.',
}

// Formata 'YYYY-MM-DD HH:mm:ss' (vindo do backend) para dd/mm/yyyy HH:mm
const formatDateTime = (value) => {
    if (value === null || value === undefined || value === '') return '-'
    const d = new Date(String(value).replace(' ', 'T'))
    if (Number.isNaN(d.getTime())) return formatDate(value)
    return d.toLocaleString('pt-BR', { timeZone: 'UTC', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

// Configuração do painel de execuções de busca de dados da obra (admin)
export const BUSCA_CONFIG = {
    title: 'Execuções de busca de dados',
    icon: MagnifyingGlassIcon,
    columns: [
        { key: 'nome_obra', label: 'Obra' },
        { key: 'codigo_obra', label: 'Código' },
        { key: 'area', label: 'Área' },
        { key: 'intervalo', label: 'Intervalo', render: (row) => `${row.intervalo_tempo ?? '-'} ${row.tipo_intervalo_tempo ?? ''}`.trim() },
        { key: 'ultima_busca', label: 'Última busca', format: formatDateTime },
        { key: 'proxima_busca', label: 'Próxima busca', format: formatDateTime },
        { key: 'data_criacao', label: 'Criado em', format: formatDateTime },
    ],
    filterOptions: [
        { value: 'nome_obra', label: 'Obra' },
        { value: 'codigo_obra', label: 'Código' },
        { value: 'area', label: 'Área' },
        { value: 'tipo_intervalo_tempo', label: 'Tipo de intervalo' },
    ],
    sortOptions: [
        { value: 'data_criacao', label: 'Criado em' },
        { value: 'ultima_busca', label: 'Última busca' },
        { value: 'proxima_busca', label: 'Próxima busca' },
        { value: 'area', label: 'Área' },
    ],
}

// Colunas curadas para o drill-down paginado (labels amigáveis ao investidor)
const currency = (v) => formatCurrency(v)
export const DETAIL_CONFIG = {
    estoque: {
        title: 'Pesquisa de Unidades',
        subtitle: 'Busque unidades do inventário e expanda para ver o vínculo com venda, contrato e situação financeira.',
        icon: ArchiveBoxIcon,
        expandable: true,
        expandIdKey: 'id',
        rowKey: 'identificador',
        columns: [
            { key: 'identificador', label: 'Unidade' },
            { key: 'descricao_status', label: 'Status' },
            { key: 'data_cadastro', label: 'Cadastro', format: formatDate },
        ],
        filterOptions: [
            { value: 'identificador', label: 'Unidade' },
            { value: 'descricao_status', label: 'Status' },
        ],
        sortOptions: [
            { value: 'data_cadastro', label: 'Cadastro' },
            { value: 'identificador', label: 'Unidade' },
            { value: 'descricao_status', label: 'Status' },
        ],
    },
    vendas: {
        title: 'Pesquisa de Contratos',
        subtitle: 'Busque contratos no período e expanda cada linha para ver unidades, parcelas recebidas e saldo a receber.',
        icon: ShoppingCartIcon,
        expandable: true,
        rowKey: 'contrato',
        // Grão de CONTRATO (1 linha por num_ven): VGV = MAX(vlrtotvenda). Sem "bloco" (sempre vazio no ERP).
        columns: [
            { key: 'contrato', label: 'Contrato' },
            { key: 'cliente', label: 'Cliente' },
            { key: 'data_ven', label: 'Data da Venda', format: formatDate },
            { key: 'unidades', label: 'Unidades' },
            { key: 'valor_venda', label: 'VGV', format: currency },
        ],
        filterOptions: [
            { value: 'num_ven', label: 'Contrato' },
            { value: 'nome_pes', label: 'Cliente' },
            { value: 'identificador_unid', label: 'Unidade' },
        ],
        sortOptions: [
            { value: 'valor_venda', label: 'VGV' },
            { value: 'data_ven', label: 'Data da Venda' },
            { value: 'contrato', label: 'Contrato' },
            { value: 'unidades', label: 'Unidades' },
        ],
    },
    recebidos: {
        title: 'Recebimentos',
        subtitle: 'Cliente, contrato, competência e valor depositado.',
        icon: ArrowDownCircleIcon,
        columns: [
            { key: 'cliente', label: 'Cliente' },
            { key: 'venda', label: 'Contrato' },
            { key: 'data_comp', label: 'Competência', format: formatDate },
            { key: 'tipo', label: 'Tipo' },
            { key: 'total_dep', label: 'Depositado', format: currency },
        ],
        filterOptions: [
            { value: 'cliente', label: 'Cliente' },
            { value: 'venda', label: 'Contrato' },
            { value: 'tipo', label: 'Tipo' },
        ],
        sortOptions: [
            { value: 'total_dep', label: 'Depositado' },
            { value: 'data_comp', label: 'Competência' },
            { value: 'cliente', label: 'Cliente' },
        ],
    },
    'a-receber': {
        title: 'Carteira por Contrato',
        subtitle: 'Contratos com saldo em aberto (período + 12 meses). Expanda para ver parcelas recebidas, a receber e vínculo com a venda.',
        icon: ClockIcon,
        expandable: true,
        rowKey: 'contrato',
        columns: [
            { key: 'cliente', label: 'Cliente' },
            { key: 'contrato', label: 'Contrato' },
            { key: 'total', label: 'Total a Receber', format: currency },
            { key: 'vencido', label: 'Vencido', format: currency },
            { key: 'a_vencer', label: 'A Vencer', format: currency },
            { key: 'parcelas', label: 'Parcelas', format: (v) => formatNumber(v) },
        ],
        filterOptions: [
            { value: 'cliente', label: 'Cliente' },
            { value: 'venda', label: 'Contrato' },
        ],
        sortOptions: [
            { value: 'total', label: 'Total a Receber' },
            { value: 'vencido', label: 'Vencido' },
            { value: 'a_vencer', label: 'A Vencer' },
            { value: 'cliente', label: 'Cliente' },
            { value: 'contrato', label: 'Contrato' },
        ],
    },
    movimentacao: {
        title: 'Lançamentos Financeiros',
        subtitle: '',
        icon: ArrowsRightLeftIcon,
        columns: [
            { key: 'macro_categoria', label: 'Macro' },
            { key: 'grupo_despesa', label: 'Grupo despesa' },
            { key: 'centro_custo', label: 'Centro de custo' },
            { key: 'natureza', label: 'Categoria' },
            { key: 'tipo', label: 'Tipo' },
            { key: 'fornecedor', label: 'Fornecedor' },
            { key: 'valor', label: 'Valor', format: currency },
            { key: 'data', label: 'Data', format: formatDate },
        ],
        filterOptions: [
            { value: 'macro_categoria', label: 'Macro' },
            { value: 'grupo_despesa', label: 'Grupo despesa' },
            { value: 'centro_custo', label: 'Centro de custo' },
            { value: 'natureza', label: 'Categoria' },
            { value: 'fornecedor', label: 'Fornecedor' },
        ],
        sortOptions: [
            { value: 'macro_categoria', label: 'Macro' },
            { value: 'grupo_despesa', label: 'Grupo despesa' },
            { value: 'centro_custo', label: 'Centro de custo' },
            { value: 'data', label: 'Data' },
            { value: 'valor', label: 'Valor' },
            { value: 'natureza', label: 'Categoria' },
        ],
    },
}
